import React, { useEffect, useState } from "react";
import { getBMPWalletIncreasements } from "../../api/getBMPWalletIncreasements";
import { WalletIncrementsResponse } from "../../models/bmp.model";
import { ArrowLeftIcon } from "./icons/ArrowLeftIcon";
import { EmptyIcon } from "./icons/EmptyIcon";

interface WalletIncreasementTableProps {
    onBack?: () => void;
}

export const WalletIncreasementTable: React.FC<WalletIncreasementTableProps> = ({ onBack }) => {
    const [data, setData] = useState<WalletIncrementsResponse | null>();
    const [isLoading, setIsLoading] = useState(true);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const result = await getBMPWalletIncreasements();
            setData(result);
        } catch (error) {
            console.error("Error fetching wallet increments:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const formatVal = (val: number) => {
        return new Intl.NumberFormat("hu-HU", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 6,
        }).format(val);
    };

    return (
        <div className="min-h-screen bg-back p-6 sm:p-10 font-sans text-dark-blue">
            <div className="max-w-[1400px] mx-auto mb-8">
                <button 
                    onClick={onBack} 
                    className="flex items-center gap-2 text-sm font-bold text-brown hover:text-dark-brown transition-colors mb-2 uppercase tracking-wide"
                >
                    <ArrowLeftIcon />
                    Vissza
                </button>
                <h1 className="text-3xl font-extrabold text-dark-blue tracking-tight">Havi befektetések összesítése</h1>
                <p className="text-light-blue mt-1">A Fix, Robot és Trading stratégiák tárcáinak változása.</p>
            </div>

            <div className="max-w-[1400px] mx-auto bg-white rounded-2xl shadow-xl overflow-hidden border border-cream">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-cream border-b border-light-brown/20 text-dark-brown">
                                <th className="p-4 font-bold text-[10px] uppercase tracking-wider">Hónap</th>
                                <th className="p-4 font-bold text-[10px] uppercase tracking-wider text-right border-x border-light-brown/10">Fix BTC</th>
                                <th className="p-4 font-bold text-[10px] uppercase tracking-wider text-right border-r border-light-brown/10">Fix ETH</th>
                                <th className="p-4 font-bold text-[10px] uppercase tracking-wider text-right border-r border-light-brown/10">Robot által termelt BTC</th>
                                <th className="p-4 font-bold text-[10px] uppercase tracking-wider text-right border-r border-light-brown/10">Robot likviditás</th>
                                <th className="p-4 font-bold text-[10px] uppercase tracking-wider text-right border-r border-light-brown/10">Trading BTC</th>
                                <th className="p-4 font-bold text-[10px] uppercase tracking-wider text-right border-r border-light-brown/10">Trading ETH</th>
                                <th className="p-4 font-bold text-[10px] uppercase tracking-wider text-right">Trading likviditás</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-ultra-light">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={8} className="p-12 text-center">
                                        <div className="animate-spin inline-block w-8 h-8 border-4 border-brown border-t-transparent rounded-full" />
                                    </td>
                                </tr>
                            ) : !data || data.months.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="p-16 text-center">
                                        <div className="flex flex-col items-center gap-3">
                                            <EmptyIcon />
                                            <h3 className="text-lg font-semibold text-dark-blue">Nincs adat.</h3>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                data.months.map((month) => (
                                    <tr key={month.label} className="hover:bg-ultra-light/50 transition-colors group">
                                        <td className="p-4 text-dark-blue font-bold text-sm whitespace-nowrap">{month.label}</td>
                                        <td className="p-4 text-right font-mono text-xs border-x border-ultra-light">{formatVal(month.fixBtc)}</td>
                                        <td className="p-4 text-right font-mono text-xs border-r border-ultra-light">{formatVal(month.fixEth)}</td>
                                        <td className="p-4 text-right font-mono text-xs border-r border-ultra-light">{formatVal(month.robotBtc)}</td>
                                        <td className="p-4 text-right font-mono text-xs border-r border-ultra-light text-dark-brown font-semibold">{formatVal(month.robotLiquidity)}</td>
                                        <td className="p-4 text-right font-mono text-xs border-r border-ultra-light">{formatVal(month.tradingBtc)}</td>
                                        <td className="p-4 text-right font-mono text-xs border-r border-ultra-light">{formatVal(month.tradingEth)}</td>
                                        <td className="p-4 text-right font-mono text-xs text-dark-brown font-semibold">{formatVal(month.tradingLiquidity)}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                        
                        {!isLoading && data && data.months.length > 0 && (
                            <tfoot className="bg-ultra-light text-dark-blue border-t-2 border-cream font-bold">
                                <tr>
                                    <td className="p-4 uppercase text-[10px] tracking-wider">Összesen</td>
                                    <td className="p-4 text-right text-xs border-x border-light-brown/10">{formatVal(data.summary.fixBtc)}</td>
                                    <td className="p-4 text-right text-xs border-r border-light-brown/10">{formatVal(data.summary.fixEth)}</td>
                                    <td className="p-4 text-right text-xs border-r border-light-brown/10">{formatVal(data.summary.robotBtc)}</td>
                                    <td className="p-4 text-right text-xs border-r border-light-brown/10 text-dark-brown">{formatVal(data.summary.robotLiquidity)}</td>
                                    <td className="p-4 text-right text-xs border-r border-light-brown/10">{formatVal(data.summary.tradingBtc)}</td>
                                    <td className="p-4 text-right text-xs border-r border-light-brown/10">{formatVal(data.summary.tradingEth)}</td>
                                    <td className="p-4 text-right text-xs text-dark-brown">{formatVal(data.summary.tradingLiquidity)}</td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );
};